import { useState } from 'react';
import {
  HelpCircle,
  Mail,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageSquare,
  BookOpen,
  Trophy,
  Users,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LegalAgreementDialog } from '@/components/legal/LegalAgreementDialog';
import { SupportRequestForm } from '@/components/support/SupportRequestForm';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/constants/support';

interface FaqItem {
  question: string;
  answer: string;
  badge?: string;
}

const faqs: FaqItem[] = [
  {
    question: 'What is the difference between a jackpot and a rodeo?',
    answer:
      'HighCall supports two event types. A jackpot is a single team roping event where you enter, pay an entry fee, and compete for a payout based on your finish. A rodeo is a multi-event production that may run over one or more performances and can include buddy group entry so a primary cardholder can sign up partners along with themselves.',
  },
  {
    question: 'How do I enter a jackpot?',
    answer:
      'Go to the Jackpots page, pick a jackpot, and choose an event. Select your position (Header or Heeler), pick a partner if the event requires one, and complete payment to finalize your spot. Your entry will appear on your Dashboard.',
  },
  {
    question: 'How do I enter a rodeo?',
    answer:
      'Open the Rodeos page and select a rodeo. If the rodeo allows buddy groups, you can either enter yourself as the primary cardholder and add partners to your group, or join an existing buddy group you have been invited to. If the rodeo does not use buddy groups, enter directly for the events you want to compete in.',
  },
  {
    question: 'How do buddy groups work?',
    answer:
      'Buddy groups apply to rodeos that allow them. The primary cardholder creates the group, enters themselves, and can add partners to the same group so everyone is entered together. Group members can be reordered and reassigned to events from the buddy group setup page. You can manage your groups from the Buddy Groups page.',
    badge: 'Rodeos',
  },
  {
    question: 'How are teams drawn?',
    answer:
      'For jackpots that use the draw system, teams are formed after entries close when a delegate or admin runs the draw — pairings can be random or weighted by handicap depending on the event ruleset. For rodeos, partners are typically chosen at entry time (either directly or through buddy groups) rather than drawn. You can view your team assignment on the event detail page once it is set.',
  },
  {
    question: 'How do handicap ratings work?',
    answer:
      'Handicaps apply to jackpots only — rodeos do not use handicap ratings. For jackpots, your handicap is assigned by an organization administrator based on your skill level and is used by the draw system to keep team pairings fair. You can view your current handicap on your profile page.',
    badge: 'Jackpots only',
  },
  {
    question: 'Can I cancel my entry?',
    answer:
      'Most events can be cancelled up until the draw takes place — once the draw runs, the entry is locked. Note that the transaction fee, if paid, is non-refundable even when the entry itself is refunded. For jackpots, contact your organization delegate or admin before the draw to request a cancellation. For rodeo buddy groups, the primary cardholder can disband or modify the group from the Buddy Groups page until the rodeo locks. Outstanding fees from past events may also block new entries until they are resolved.',
  },
  {
    question: 'What payment methods are accepted?',
    answer:
      'Entry fees for both jackpots and rodeos can be paid by card or bank transfer (ACH), processed securely through Stripe. Members may also use Pay Later or Cash at Event when an organizer enables those options. If you have outstanding fees from a previous event, you will need to clear them before entering a new event.',
  },
];

const helpLinks = [
  {
    icon: Trophy,
    title: 'Browse Jackpots',
    description: 'Find and enter upcoming team roping jackpots.',
    href: '/jackpots',
    linkText: 'Go to Jackpots',
  },
  {
    icon: Users,
    title: 'Browse Rodeos',
    description: 'Find rodeos and manage buddy group entries.',
    href: '/rodeos',
    linkText: 'Go to Rodeos',
  },
  {
    icon: BookOpen,
    title: 'Your Profile',
    description: 'Complete your profile and view your handicap.',
    href: '/profile',
    linkText: 'Go to Profile',
  },
  {
    icon: MessageSquare,
    title: 'Dashboard',
    description: 'View your upcoming events, stats, and history.',
    href: '/dashboard',
    linkText: 'Go to Dashboard',
  },
];

function FaqAccordion({ item }: { item: FaqItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between gap-3 py-4 text-left text-sm font-medium hover:text-primary transition-colors"
      >
        <span className="flex items-center gap-2">
          {item.question}
          {item.badge && (
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
              {item.badge}
            </Badge>
          )}
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-sm text-muted-foreground">{item.answer}</div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const [agreementOpen, setAgreementOpen] = useState(false);

  return (
    <div className="space-y-6">
      <LegalAgreementDialog open={agreementOpen} onOpenChange={setAgreementOpen} />
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <HelpCircle className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Help & Support</h1>
          <p className="text-sm text-muted-foreground">
            Answers for jackpots, rodeos, registrations, and payments
          </p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {helpLinks.map((link) => (
          <Card key={link.title}>
            <CardContent className="flex flex-col items-center gap-3 p-6 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <link.icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{link.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {link.description}
                </p>
              </div>
              <a href={link.href}>
                <Button variant="outline" size="sm">
                  {link.linkText}
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
              </a>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            {faqs.map((faq) => (
              <FaqAccordion key={faq.question} item={faq} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Legal */}
      <Card>
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center sm:flex-row sm:text-left">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold">Standard License &amp; Data-Usage Agreement</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Review the agreement you accepted when you signed up — how HighCall
              uses your data, and the terms of using the service.
            </p>
          </div>
          <Button variant="outline" onClick={() => setAgreementOpen(true)}>
            <FileText className="mr-2 h-4 w-4" />
            Read the agreement
          </Button>
        </CardContent>
      </Card>

      {/* Contact form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5 text-primary" />
            Still need help? Send us a message
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Can't find what you're looking for? Send the HighCall support team a
            note and we'll get back to you by email. You can also reach us at{' '}
            <a href={SUPPORT_MAILTO} className="underline hover:text-primary">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </CardHeader>
        <CardContent>
          <SupportRequestForm />
        </CardContent>
      </Card>
    </div>
  );
}
